<template>
  <div class="manage-transport-modal">
    <div class="mb-6">
      <h3 class="text-xl font-semibold mb-2">
        Manage Transport
      </h3>
      <p class="text-sm text-surface-600 dark:text-surface-400">
        Equipment List Line: <strong>{{ equipmentListLineName }}</strong>
      </p>
    </div>

    <!-- Transport Type -->
    <div class="mb-6">
      <label class="block text-sm font-medium mb-2">Transport Type</label>
      <Dropdown
        v-model="selectedTransportTypeId"
        :options="transportTypeOptions"
        option-label="name"
        option-value="id"
        placeholder="Select transport type"
        class="w-full"
        :loading="transportTypesLoading"
      />
    </div>

    <div class="flex justify-end gap-2 mt-6">
      <Button
        label="Cancel"
        severity="secondary"
        text
        @click="closeDialog"
      />
      <Button
        label="Save"
        icon="pi pi-check"
        :loading="saving"
        @click="handleSave"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { projectStore } from '../projectStore';
import { getTransportTypes } from '@/services/transportTypesService';
import { updateEquipmentListContent } from '@/services/projectService';
import { useToastService } from '@/composables/useToastService';

const props = defineProps({
  equipmentListLineId: {
    type: [String, Number],
    required: true,
  },
  equipmentListLineName: {
    type: String,
    default: 'Unknown Item',
  },
  transportTypeId: {
    type: [String, Number],
    default: null,
  },
  transportTypeName: {
    type: String,
    default: '',
  },
});

const toast = useToastService();

const transportTypeOptions = ref([]);
const transportTypesLoading = ref(false);
const selectedTransportTypeId = ref(null);
const saving = ref(false);

const selectedTransportType = computed(() => {
  const id = selectedTransportTypeId.value;
  if (id == null) return null;
  return transportTypeOptions.value.find((t) => t.id === id || String(t.id) === String(id));
});

const loadTransportTypes = async () => {
  transportTypesLoading.value = true;
  try {
    const response = await getTransportTypes();
    const data = response?.data ?? response ?? [];
    transportTypeOptions.value = Array.isArray(data) ? data : [];
    if (props.transportTypeId != null && !selectedTransportTypeId.value) {
      selectedTransportTypeId.value = props.transportTypeId;
    }
    if (transportTypeOptions.value.length > 0 && selectedTransportTypeId.value == null && props.transportTypeName) {
      const match = transportTypeOptions.value.find(
        (t) => t.name === props.transportTypeName || String(t.id) === String(props.transportTypeId)
      );
      if (match) selectedTransportTypeId.value = match.id;
    }
  } catch (err) {
    console.error('[ManageTransportModal] Error loading transport types:', err);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to load transport types.',
      life: 3000,
    });
    transportTypeOptions.value = [];
  } finally {
    transportTypesLoading.value = false;
  }
};

const closeDialog = () => {
  projectStore.closeModal();
};

const handleSave = async () => {
  const projectId = projectStore.currentProjectId;
  const equipmentListId = projectStore.currentEquipmentListId;
  const contentId = props.equipmentListLineId;

  if (!projectId || !equipmentListId || !contentId) {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Missing project or equipment list context.',
      life: 3000,
    });
    return;
  }

  const type = selectedTransportType.value;
  const name = type?.name ?? props.transportTypeName ?? 'Transport';

  const updateData = {
    name,
    additional_data: {
      transport_type_id: selectedTransportTypeId.value,
    },
  };

  saving.value = true;
  try {
    await updateEquipmentListContent(projectId, equipmentListId, contentId, updateData);
    toast.add({
      severity: 'success',
      summary: 'Saved',
      detail: 'Transport type updated successfully.',
      life: 3000,
    });
    if (projectStore.refreshFunction) {
      await projectStore.refreshFunction();
    }
    closeDialog();
  } catch (err) {
    console.error('[ManageTransportModal] Error saving:', err);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: err.message || 'Failed to update transport.',
      life: 5000,
    });
  } finally {
    saving.value = false;
  }
};

onMounted(() => {
  selectedTransportTypeId.value = props.transportTypeId;
  loadTransportTypes();
});
</script>
